import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { ActivityHeatmap } from '@/components/activity/activity-heatmap'
import { Pulse } from '@/components/activity/activity-panel'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { RoutePaths } from '@/routes/routes'
import {
  useQueryGetOrgActivityHeatmap,
  useQueryGetUsersActivity
} from '@/services'
import { addDays } from '@/utils/activity'
import {
  compareActivityTime,
  formatActivityTime,
  getUserActivityKey
} from '@/utils/user-activity'

/**
 * Relative time with the absolute timestamp always visible (no tooltip),
 * so the pane reads the same in a screenshot.
 */
export const InlineActivityTime = ({
  value,
  size = 'sm'
}: {
  value?: string | null
  size?: 'sm' | 'lg'
}) => {
  const { relative, absolute } = formatActivityTime(value)

  return (
    <div className="leading-tight">
      <div className={size === 'lg' ? 'text-lg font-semibold' : 'text-sm'}>
        {relative}
      </div>
      {absolute && (
        <div className="mt-0.5 text-xs font-normal text-muted-foreground/70 tabular-nums">
          {absolute}
        </div>
      )}
    </div>
  )
}

const Stat = ({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className="space-y-2 rounded-lg bg-muted/30 p-4">
    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80">
      {label}
    </div>
    <div className="text-lg font-semibold tabular-nums">{children}</div>
  </div>
)

/**
 * Per-user activity for one org membership, shared by the org Metrics drawer and
 * the user details Activity tab: stats, binary 12-month heatmap, memberships.
 */
export const UserActivityPane = ({
  orgId,
  userId
}: {
  orgId: string
  userId: string
}) => {
  const heatmap = useQueryGetOrgActivityHeatmap(orgId, { user_id: userId })
  // no org filter: the same user id is shared across all of the user's org memberships
  const { data: activityByUser, isLoading: isActivityLoading } =
    useQueryGetUsersActivity([userId])

  const current = activityByUser?.[getUserActivityKey(orgId, userId)]
  // most recently active first, memberships without activity last
  const memberships = Object.values(activityByUser ?? {}).sort((a, b) =>
    a.last_seen_at && b.last_seen_at
      ? compareActivityTime(b.last_seen_at, a.last_seen_at)
      : compareActivityTime(a.last_seen_at, b.last_seen_at)
  )

  const activeDays = useMemo(() => {
    const data = heatmap.data

    if (!data) return undefined

    const since30 = addDays(data.to, -29)
    const since90 = addDays(data.to, -89)
    const active = data.days.filter((d) => d.value > 0)

    return {
      d30: active.filter((d) => d.day >= since30).length,
      d90: active.filter((d) => d.day >= since90).length
    }
  }, [heatmap.data])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Last login">
          {isActivityLoading ? (
            <Pulse className="h-6 w-20" />
          ) : (
            <InlineActivityTime size="lg" value={current?.last_login_at} />
          )}
        </Stat>
        <Stat label="Last activity">
          {isActivityLoading ? (
            <Pulse className="h-6 w-20" />
          ) : (
            <InlineActivityTime size="lg" value={current?.last_seen_at} />
          )}
        </Stat>
        <Stat label="Active days 30d">
          {activeDays ? (
            <>
              {activeDays.d30}
              <span className="text-muted-foreground/50"> / 30</span>
            </>
          ) : (
            <Pulse className="h-6 w-16" />
          )}
        </Stat>
        <Stat label="Active days 90d">
          {activeDays ? (
            <>
              {activeDays.d90}
              <span className="text-muted-foreground/50"> / 90</span>
            </>
          ) : (
            <Pulse className="h-6 w-16" />
          )}
        </Stat>
      </div>

      <section className="space-y-2">
        <div>
          <h3 className="text-sm font-semibold">
            Active days · last 12 months
          </h3>
          <p className="text-xs text-muted-foreground/70">
            One square per day: active or not. No times of day are shown for
            individual users.
          </p>
        </div>
        {heatmap.data ? (
          <ActivityHeatmap
            ariaLabel="Active days of this user in the last 12 months"
            binary
            compact
            days={heatmap.data.days}
            from={heatmap.data.from}
            to={heatmap.data.to}
          />
        ) : heatmap.isError ? (
          <p className="text-sm text-muted-foreground/70">
            Heatmap could not be loaded.
          </p>
        ) : (
          <Pulse className="h-24 w-full" />
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold">Organizations</h3>
        {isActivityLoading ? (
          <Pulse className="h-16 w-full" />
        ) : memberships.length === 0 ? (
          <p className="rounded-lg bg-muted/30 p-4 text-sm text-muted-foreground/70">
            No activity recorded since tracking started.
          </p>
        ) : (
          <div className="overflow-hidden rounded-lg bg-muted/30">
            <Table>
              <TableHeader>
                <TableRow className="border-border/30 hover:bg-transparent">
                  <TableHead>Org</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead>First seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {memberships.map((row) => (
                  <TableRow
                    className="border-border/30 hover:bg-muted/20"
                    key={row.org_id}
                  >
                    <TableCell>
                      <Link
                        className="font-mono text-sm underline-offset-4 hover:underline"
                        to={`${RoutePaths.ORGANIZATIONS}/${row.org_id}?tab=activity`}
                      >
                        {row.org_id}
                      </Link>
                      {row.org_id === orgId && (
                        <Badge className="ml-2 border-transparent bg-secondary/20 text-secondary hover:bg-secondary/20">
                          current
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <InlineActivityTime value={row.last_login_at} />
                    </TableCell>
                    <TableCell>
                      <InlineActivityTime value={row.last_seen_at} />
                    </TableCell>
                    <TableCell>
                      <InlineActivityTime value={row.first_seen_at} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  )
}
